import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import LanguageDetector from 'i18next-browser-languagedetector';
import zhCN from 'antd/locale/zh_CN';
import enUS from 'antd/locale/en_US';
import zhCommon from './zh/common.json';
import enCommon from './en/common.json';
import zhHome from './zh/home.json';
import enHome from './en/home.json';

// Lazy-load translation files via explicit dynamic imports.
// At startup we only bundle the tiny `common` namespace for both languages;
// all other namespaces are fetched on demand through the custom Vite backend.
const SUPPORTED_LANGUAGES = ['zh', 'en'];
const NAMESPACES = [
  'aichat',
  'aigc',
  'ailab',
  'common',
  'components',
  'games',
  'home',
  'industry',
  'modelTrainCommon',
  'modelTrainDatasets',
  'modelTrainModels',
  'modelTrainProjects',
  'principles',
  'qinghua',
  'routes',
  'training',
];

const translationLoaders = {
  zh: {
    aichat: () => import('./zh/aichat.json'),
    aigc: () => import('./zh/aigc.json'),
    ailab: () => import('./zh/ailab.json'),
    common: () => import('./zh/common.json'),
    components: () => import('./zh/components.json'),
    games: () => import('./zh/games.json'),
    home: () => import('./zh/home.json'),
    industry: () => import('./zh/industry.json'),
    modelTrainCommon: () => import('./zh/modelTrainCommon.json'),
    modelTrainDatasets: () => import('./zh/modelTrainDatasets.json'),
    modelTrainModels: () => import('./zh/modelTrainModels.json'),
    modelTrainProjects: () => import('./zh/modelTrainProjects.json'),
    principles: () => import('./zh/principles.json'),
    qinghua: () => import('./zh/qinghua.json'),
    routes: () => import('./zh/routes.json'),
    training: () => import('./zh/training.json'),
  },
  en: {
    aichat: () => import('./en/aichat.json'),
    aigc: () => import('./en/aigc.json'),
    ailab: () => import('./en/ailab.json'),
    common: () => import('./en/common.json'),
    components: () => import('./en/components.json'),
    games: () => import('./en/games.json'),
    home: () => import('./en/home.json'),
    industry: () => import('./en/industry.json'),
    modelTrainCommon: () => import('./en/modelTrainCommon.json'),
    modelTrainDatasets: () => import('./en/modelTrainDatasets.json'),
    modelTrainModels: () => import('./en/modelTrainModels.json'),
    modelTrainProjects: () => import('./en/modelTrainProjects.json'),
    principles: () => import('./en/principles.json'),
    qinghua: () => import('./en/qinghua.json'),
    routes: () => import('./en/routes.json'),
    training: () => import('./en/training.json'),
  },
};

function normalizeLanguage(lng) {
  if (!lng) return 'zh';
  const base = String(lng).split('-')[0];
  return SUPPORTED_LANGUAGES.includes(base) ? base : 'zh';
}

// Custom i18next backend that resolves translation files through Vite's dynamic imports.
const viteBackend = {
  type: 'backend',
  init() {},
  read(language, namespace, callback) {
    const loader = translationLoaders[language]?.[namespace];
    if (!loader) {
      callback(null, false);
      return;
    }
    loader()
      .then((module) => callback(null, module.default || module))
      .catch((error) => callback(error, false));
  },
};

const detector = new LanguageDetector(null, {
  order: ['localStorage', 'navigator', 'htmlTag'],
  lookupLocalStorage: 'i18nextLng',
  caches: ['localStorage'],
});

const detectedLng = normalizeLanguage(detector.detect());

i18n
  .use(viteBackend)
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    resources: {
      zh: { common: zhCommon, home: zhHome },
      en: { common: enCommon, home: enHome },
    },
    lng: detectedLng,
    fallbackLng: 'zh',
    debug: false,
    defaultNS: 'common',
    ns: NAMESPACES,
    partialBundledLanguages: true,
    interpolation: {
      escapeValue: false, // React already escapes
    },
    detection: {
      order: ['localStorage', 'navigator', 'htmlTag'],
      lookupLocalStorage: 'i18nextLng',
      caches: ['localStorage'],
    },
    react: {
      useSuspense: false,
    },
  });

// Map i18next language codes to antd locale imports
export const antdLocaleMap = {
  zh: zhCN,
  'zh-CN': zhCN,
  en: enUS,
  'en-US': enUS,
};

export default i18n;
